import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCampaign } from "../Logic/CampaignContext";

import knowledgeData from "../All Cards/Knowledge";
import philosophyData from "../All Cards/Philosophy";
import innovationsData from "../All Cards/Innovation";
import settlementLocationsData from "../All Cards/SettlementLocation";
import gearCardsData from "../All Cards/GearCards";

import { QUARRY_BY_NODE, NEMESIS_BY_NODE, CC_REWARDS, hasPillar, numOrZero, clampInt, romanToNumber, parseLegacyList, ensureBoolArrayLen, toggleBoolAt, countChecked, getSurvivorQuickStats, prettyKeyToTitle } from "./settlementRules";
import { SectionBox, TwoCharNumber, CheckboxGrid, CardText, InnovationCardText } from "./SheetControls";
import SurvivorsPanel from "./SurvivorsPanel";

export default function SettlementSheet() {
  const nav = useNavigate();
  const {
    current,
    error,
    addSurvivor,
    updateSurvivor,
    removeSurvivor,
    updateSettlement,
    toggleDepartingSurvivor,
    maxDeparting,
  } = useCampaign();

  const [newName, setNewName] = useState("");

  const [openSheetIds, setOpenSheetIds] = useState(() => new Set());

  // gear add dropdown state
  const [gearToAdd, setGearToAdd] = useState("");

  const pillars = current?.pillars || [];
  const useArc = hasPillar(pillars, "arc survivors");
  const hasSeedPatterns = hasPillar(pillars, "seed patterns");
  const limit = maxDeparting();

  const survivors = Array.isArray(current?.survivors) ? current.survivors : [];

  // ✅ Used for per-survivor localStorage keys (must be defined before any hooks use it)
  const campaignKeyBase = `kdm__campaign__${current?.id || current?.settlementName || "__no_campaign__"}`;


  const survivorsWithStatus = useMemo(() => {
    return survivors.map((s) => {
      const qs = s.__qs || getSurvivorQuickStats(s, useArc);
      return { ...s, __qs: qs };
    });
  }, [survivors, useArc]);

  const { aliveSurvivors, deceasedSurvivors } = useMemo(() => {
    const alive = [];
    const dead = [];
    for (const s of survivorsWithStatus) {
      if (s.__qs?.deceased) dead.push(s);
      else alive.push(s);
    }
    const byName = (a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
    alive.sort(byName);
    dead.sort(byName);
    return { aliveSurvivors: alive, deceasedSurvivors: dead };
  }, [survivorsWithStatus]);
  const departingIds = Array.isArray(current?.departingSurvivorIds) ? current.departingSurvivorIds : [];
  const settlement = current?.settlement || {};

  const showLanternResearch =
    current?.campaignType === "People of the Lantern" || current?.campaignType === "People of the Sun";
  const isDreamKeeper = current?.campaignType === "People of the Dream Keeper";

  const InlineStyleGuard = () => (
    <style>{`
      .twoCharBox{
        width: 44px !important;
        min-width: 44px !important;
        max-width: 44px !important;
        text-align: center;
      }
      .qtyBox{
        width: 28px !important;
        min-width: 28px !important;
        max-width: 28px !important;
        text-align: center;
      }
    `}</style>
  );

  // ==========================================================
  // Monster filtering (based on current.monsters)
  // ==========================================================
  const selectedMonstersRaw = useMemo(() => (Array.isArray(current?.monsters) ? current.monsters : []), [current?.monsters]);

  const MONSTER_ALIASES = useMemo(
    () => ({
      "Smog Singer": "Smog Singers",
      "Smog Singers": "Smog Singers",
    }),
    []
  );

  const selectedMonsterSet = useMemo(() => {
    const s = new Set();
    for (const m of selectedMonstersRaw) {
      const nm = String(m || "").trim();
      if (!nm) continue;
      const aliased = MONSTER_ALIASES[nm] || nm;
      s.add(aliased);
      s.add(nm);
    }
    return s;
  }, [selectedMonstersRaw, MONSTER_ALIASES]);

  const allQuarryNamesFiltered = useMemo(() => {
    return Object.entries(QUARRY_BY_NODE)
      .flatMap(([node, list]) =>
        list
          .filter((n) => selectedMonsterSet.has(n))
          .map((n) => ({ name: n, node: Number(node) }))
      )
      .sort((a, b) => a.node - b.node || a.name.localeCompare(b.name));
  }, [selectedMonsterSet]);

  const allNemesisNamesFiltered = useMemo(() => {
    return Object.entries(NEMESIS_BY_NODE)
      .flatMap(([node, list]) =>
        list
          .filter((n) => selectedMonsterSet.has(n))
          .map((n) => ({ name: n, node: Number(node) }))
      )
      .sort((a, b) => a.node - b.node || a.name.localeCompare(b.name));
  }, [selectedMonsterSet]);

  // =========================
  // Core checkbox structures
  // =========================
  const deathBoxes = useMemo(() => {
    if (Array.isArray(settlement.deathBoxes)) return ensureBoolArrayLen(settlement.deathBoxes, 56);
    const legacy = clampInt(settlement.deathCount, 0, 56);
    return Array.from({ length: 56 }, (_, i) => i < legacy);
  }, [settlement.deathBoxes, settlement.deathCount]);

  const lostSettlementBoxes = useMemo(
    () => ensureBoolArrayLen(settlement.lostSettlementBoxes, 20),
    [settlement.lostSettlementBoxes]
  );

  const quarriesChecked =
    settlement.quarriesChecked && typeof settlement.quarriesChecked === "object" ? settlement.quarriesChecked : {};

  const nemesisProgress =
    settlement.nemesisProgress && typeof settlement.nemesisProgress === "object" ? settlement.nemesisProgress : {};

  const milestoneChecks =
    settlement.campaignMilestones && typeof settlement.campaignMilestones === "object"
      ? settlement.campaignMilestones
      : {};

  const principles = settlement.principles && typeof settlement.principles === "object" ? settlement.principles : {};

  // =========================
  // Innovations (dropdown rows) + used for Principles card text
  // =========================
  const innovationsByName = useMemo(() => {
    const map = {};
    const list = Array.isArray(innovationsData) ? innovationsData : [];
    for (const c of list) {
      const nm = String(c?.name || c?.title || "").trim();
      if (!nm) continue;
      map[nm] = c;
    }
    return map;
  }, []);

  const innovationNames = useMemo(
    () => Object.keys(innovationsByName).sort((a, b) => a.localeCompare(b)),
    [innovationsByName]
  );

  const getInnovationEntry = (name) => (name ? innovationsByName[name] || null : null);

  const innovationRows = useMemo(() => {
    if (Array.isArray(settlement.innovations)) return settlement.innovations;
    const legacyNames = parseLegacyList(settlement.innovations);
    return legacyNames.map((nm) => ({ id: crypto.randomUUID(), name: nm }));
  }, [settlement.innovations]);

  const ensureAtLeastOneInnovationRow = () => {
    if (innovationRows.length > 0) return;
    updateSettlement({ innovations: [{ id: crypto.randomUUID(), name: "" }] });
  };

  const addInnovationRow = () =>
    updateSettlement({ innovations: [...innovationRows, { id: crypto.randomUUID(), name: "" }] });

  const removeInnovationRow = (id) => {
    const next = innovationRows.filter((r) => r.id !== id);
    updateSettlement({ innovations: next.length ? next : [{ id: crypto.randomUUID(), name: "" }] });
  };

  const updateInnovationRowName = (id, name) => {
    updateSettlement({ innovations: innovationRows.map((r) => (r.id === id ? { ...r, name } : r)) });
  };

  // =========================
  // Settlement Locations (dropdown rows)
  // =========================
  const locationByKey = useMemo(() => {
    const map = {};
    const list = Array.isArray(settlementLocationsData) ? settlementLocationsData : [];
    for (const loc of list) {
      const key = String(loc?.name || "").trim();
      if (!key) continue;
      map[key] = loc;
    }
    return map;
  }, []);

  const settlementLocationOptions = useMemo(() => {
    return Object.keys(locationByKey).sort((a, b) => prettyKeyToTitle(a).localeCompare(prettyKeyToTitle(b)));
  }, [locationByKey]);

  const locationRows = useMemo(() => {
    if (Array.isArray(settlement.settlementLocations)) return settlement.settlementLocations;
    const legacyNames = parseLegacyList(settlement.settlementLocations);
    return legacyNames.map((nm) => ({ id: crypto.randomUUID(), name: nm }));
  }, [settlement.settlementLocations]);

  const ensureAtLeastOneLocationRow = () => {
    if (locationRows.length > 0) return;
    updateSettlement({ settlementLocations: [{ id: crypto.randomUUID(), name: "" }] });
  };

  const addLocationRow = () =>
    updateSettlement({ settlementLocations: [...locationRows, { id: crypto.randomUUID(), name: "" }] });

  const removeLocationRow = (id) => {
    const next = locationRows.filter((r) => r.id !== id);
    updateSettlement({ settlementLocations: next.length ? next : [{ id: crypto.randomUUID(), name: "" }] });
  };

  const updateLocationRowName = (id, name) => {
    updateSettlement({ settlementLocations: locationRows.map((r) => (r.id === id ? { ...r, name } : r)) });
  };

  // =========================
  // Gear Storage (ONE dropdown + grouped by craftLocation + qty 1-9)
  // =========================
  const gearByName = useMemo(() => {
    const map = {};
    const list = Array.isArray(gearCardsData) ? gearCardsData : [];
    for (const c of list) {
      const nm = String(c?.name || "").trim();
      if (!nm) continue;
      map[nm] = c;
    }
    return map;
  }, []);

  const gearNames = useMemo(() => Object.keys(gearByName).sort((a, b) => a.localeCompare(b)), [gearByName]);

  const gearStorageItems = useMemo(() => {
    if (Array.isArray(settlement.gearStorageItems)) {
      return settlement.gearStorageItems.map((x) => ({
        ...x,
        qty: clampInt(x.qty ?? 1, 1, 9),
      }));
    }
    const legacy = parseLegacyList(settlement.gearStorage);
    return legacy.map((nm) => ({ id: crypto.randomUUID(), name: nm, qty: 1 }));
  }, [settlement.gearStorageItems, settlement.gearStorage]);

  const gearOptionLabel = (name) => {
    const entry = gearByName[name];
    const loc = String(entry?.craftLocation || "").trim();
    return `${name}${loc ? ` (${loc})` : ""}`;
  };

  const addSelectedGear = () => {
    const name = String(gearToAdd || "").trim();
    if (!name) return;

    const existingIdx = gearStorageItems.findIndex((x) => x.name === name);
    if (existingIdx >= 0) {
      const next = gearStorageItems.map((x, idx) =>
        idx === existingIdx ? { ...x, qty: clampInt((x.qty ?? 1) + 1, 1, 9) } : x
      );
      updateSettlement({ gearStorageItems: next });
    } else {
      updateSettlement({
        gearStorageItems: [...gearStorageItems, { id: crypto.randomUUID(), name, qty: 1 }],
      });
    }

    setGearToAdd("");
  };

  const removeGearItem = (id) => {
    updateSettlement({ gearStorageItems: gearStorageItems.filter((x) => x.id !== id) });
  };

  const updateGearQty = (id, qty) => {
    const q = clampInt(qty, 1, 9);
    updateSettlement({
      gearStorageItems: gearStorageItems.map((x) => (x.id === id ? { ...x, qty: q } : x)),
    });
  };

  const gearGroups = useMemo(() => {
    const groups = new Map();
    for (const item of gearStorageItems) {
      const entry = item?.name ? gearByName[item.name] : null;
      const loc = String(entry?.craftLocation || "unknown").trim() || "unknown";
      if (!groups.has(loc)) groups.set(loc, []);
      groups.get(loc).push(item);
    }

    const sorted = Array.from(groups.entries()).map(([loc, items]) => {
      const sortedItems = items.slice().sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      return { loc, items: sortedItems };
    });

    sorted.sort((a, b) => String(a.loc).toLowerCase().localeCompare(String(b.loc).toLowerCase()));
    return sorted;
  }, [gearStorageItems, gearByName]);

  // =========================
  // Philosophy / Knowledge (Arc)
  // =========================
  const philosophyByName = useMemo(() => {
    const grouped = {};
    for (const p of philosophyData || []) {
      if (!p) continue;
      const nm = String(p.name || p.title || "").trim();
      if (!nm) continue;
      grouped[nm] = p;
    }
    return grouped;
  }, []);

  const philosophyNames = useMemo(
    () => Object.keys(philosophyByName).sort((a, b) => a.localeCompare(b)),
    [philosophyByName]
  );

  const philosophyRows = useMemo(() => {
    if (Array.isArray(settlement.philosophies)) return settlement.philosophies;
    const legacyNames = parseLegacyList(settlement.philosophies);
    return legacyNames.map((nm) => ({ id: crypto.randomUUID(), name: nm }));
  }, [settlement.philosophies]);

  const ensureAtLeastOnePhilosophyRow = () => {
    if (philosophyRows.length > 0) return;
    updateSettlement({ philosophies: [{ id: crypto.randomUUID(), name: "" }] });
  };

  const addPhilosophyRow = () =>
    updateSettlement({ philosophies: [...philosophyRows, { id: crypto.randomUUID(), name: "" }] });

  const removePhilosophyRow = (id) => {
    const next = philosophyRows.filter((r) => r.id !== id);
    updateSettlement({ philosophies: next.length ? next : [{ id: crypto.randomUUID(), name: "" }] });
  };

  const updatePhilosophyRowName = (id, name) => {
    updateSettlement({ philosophies: philosophyRows.map((r) => (r.id === id ? { ...r, name } : r)) });
  };

  const getPhilosophyEntry = (name) => (name ? philosophyByName[name] || null : null);

  const knowledgeByName = useMemo(() => {
    const grouped = {};
    for (const k of knowledgeData || []) {
      if (!k || !k.name) continue;
      if (!grouped[k.name]) grouped[k.name] = [];
      grouped[k.name].push(k);
    }
    Object.keys(grouped).forEach((name) => {
      grouped[name].sort((a, b) => romanToNumber(a.level) - romanToNumber(b.level));
    });
    return grouped;
  }, []);

  const knowledgeNames = useMemo(
    () => Object.keys(knowledgeByName).sort((a, b) => a.localeCompare(b)),
    [knowledgeByName]
  );

  const settlementKnowledges = Array.isArray(settlement.knowledges) ? settlement.knowledges : [];

  const ensureAtLeastOneKnowledgeRow = () => {
    if (settlementKnowledges.length > 0) return;
    updateSettlement({ knowledges: [{ id: crypto.randomUUID(), name: "", level: "" }] });
  };

  const addKnowledgeRow = () =>
    updateSettlement({ knowledges: [...settlementKnowledges, { id: crypto.randomUUID(), name: "", level: "" }] });

  const removeKnowledgeRow = (id) => {
    const next = settlementKnowledges.filter((r) => r.id !== id);
    updateSettlement({ knowledges: next.length ? next : [{ id: crypto.randomUUID(), name: "", level: "" }] });
  };

  const updateKnowledgeRow = (id, patch) => {
    updateSettlement({ knowledges: settlementKnowledges.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  };

  const getKnowledgeEntry = (name, level) => {
    if (!name) return null;
    const list = knowledgeByName[name] || [];
    if (list.length === 0) return null;

    const cleanedLevel = String(level || "").trim();
    if (!cleanedLevel) return list.length === 1 ? list[0] : null;

    return list.find((k) => String(k.level || "").trim() === cleanedLevel) || null;
  };

  // =========================
  // Arc Survivors CC tables state
  // =========================
  const ccRewardsChecked =
    settlement.ccRewardsChecked && typeof settlement.ccRewardsChecked === "object" ? settlement.ccRewardsChecked : {};

  const ccQuarrySlots = Array.isArray(settlement.ccQuarrySlots)
    ? settlement.ccQuarrySlots
    : [
        { id: crypto.randomUUID(), name: "" },
        { id: crypto.randomUUID(), name: "" },
        { id: crypto.randomUUID(), name: "" },
        { id: crypto.randomUUID(), name: "" },
      ];

  const ccNemesisSlots = Array.isArray(settlement.ccNemesisSlots)
    ? settlement.ccNemesisSlots
    : [
        { id: crypto.randomUUID(), name: "" },
        { id: crypto.randomUUID(), name: "" },
        { id: crypto.randomUUID(), name: "" },
        { id: crypto.randomUUID(), name: "" },
        { id: crypto.randomUUID(), name: "" },
      ];

  const ccQuarryChecks = useMemo(
    () => ensureBoolArrayLen(settlement.ccQuarryChecks, 4 * 7),
    [settlement.ccQuarryChecks]
  );

  const ccNemesisChecks = useMemo(
    () => ensureBoolArrayLen(settlement.ccNemesisChecks, 5 * 3),
    [settlement.ccNemesisChecks]
  );

  const ccQuarryOptions = useMemo(() => {
    const base = allQuarryNamesFiltered.map((x) => x.name);
    return Array.from(new Set(base)).sort((a, b) => a.localeCompare(b));
  }, [allQuarryNamesFiltered]);

  const ccNemesisOptions = useMemo(() => {
    const base = allNemesisNamesFiltered.map((x) => x.name);
    return Array.from(new Set(base)).sort((a, b) => a.localeCompare(b));
  }, [allNemesisNamesFiltered]);

  // =========================
  // Survivors actions
  // =========================
  const handleAdd = () => {
    if (!newName.trim()) return;
    addSurvivor(newName);
    setNewName("");
  };

  const toggleDepartingWithGuard = (survivorId) => {
    const isOn = departingIds.includes(survivorId);
    if (!isOn && departingIds.length >= limit) {
      alert(`You can only select ${limit} departing survivors for this campaign.`);
      return;
    }
    toggleDepartingSurvivor(survivorId);
  };

  const toggleInlineSheet = (survivorId) => {
    setOpenSheetIds((prev) => {
      const next = new Set(prev);
      if (next.has(survivorId)) next.delete(survivorId);
      else next.add(survivorId);
      return next;
    });
  };

  const closeAllSheets = () => setOpenSheetIds(new Set());
  const openAllSheets = () => {
    // Open all NON-deceased survivor sheets
    const ids = aliveSurvivors.map((s) => s.id);
    setOpenSheetIds(new Set(ids));
  };


  if (!current?.settlementName) {
    return (
      <div style={{ padding: 16 }}>
        <h2>Settlement Sheet</h2>
        <div style={{ opacity: 0.85 }}>No campaign loaded.</div>
      </div>
    );
  }


  // ============================
  // ✅ RESTORED: FULL Arc Survivors block (tables + philosophy + knowledge)
  // ============================
  const ArcSurvivorsBlock = useArc ? (
    <SectionBox title="Arc Survivors">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <b>Collective Cognition</b>
        <TwoCharNumber
          value={settlement.collectiveCognition}
          onChange={(v) => updateSettlement({ collectiveCognition: v })}
          title="Collective Cognition (0-99)"
        />
      </div>
      <div style={{ fontSize: 13, opacity: 0.85, marginTop: 6 }}>
        The settlement&apos;s total CC is based on its victories
      </div>

      <div style={{ marginTop: 12 }}>
        <b>Settlement Victories</b>
        <div style={{ fontSize: 13, opacity: 0.85, marginTop: 3 }}>
          Write the name of each monster in your campaign. After a victorious showdown, fill a box for the defeated
          monster&apos;s level and update your total collective cognition.
        </div>
      </div>

      {/* Quarry table */}
      <div style={{ marginTop: 10 }}>
        <b>Quarry</b>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "240px 90px 120px 140px 140px",
            gap: 8,
            marginTop: 8,
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: 700 }}>Monster</div>
          <div style={{ fontWeight: 700 }}>
            Prologue
            <br />
            1 CC
          </div>
          <div style={{ fontWeight: 700 }}>
            Lvl 1
            <br />
            n1 CC
          </div>
          <div style={{ fontWeight: 700 }}>
            Lvl 2
            <br />
            n2 CC
          </div>
          <div style={{ fontWeight: 700 }}>
            Lvl 3
            <br />
            n3 CC
          </div>

          {ccQuarrySlots.map((slot, rowIdx) => {
            const base = rowIdx * 7;

            const b2 = ccQuarryChecks[base + 0];
            const c1 = ccQuarryChecks[base + 1];
            const d1 = ccQuarryChecks[base + 2];
            const d2 = ccQuarryChecks[base + 3];
            const e1 = ccQuarryChecks[base + 4];
            const e2 = ccQuarryChecks[base + 5];
            const e3 = ccQuarryChecks[base + 6];

            const setCheck = (offset) =>
              updateSettlement({ ccQuarryChecks: toggleBoolAt(ccQuarryChecks, base + offset) });

            const setSlotName = (name) => {
              const next = ccQuarrySlots.map((s) => (s.id === slot.id ? { ...s, name } : s));
              updateSettlement({ ccQuarrySlots: next });
            };

            return (
              <React.Fragment key={slot.id}>
                <select value={slot.name || ""} onChange={(e) => setSlotName(e.target.value)} style={{ padding: 8 }}>
                  <option value="">Select Quarry</option>
                  {ccQuarryOptions.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <input type="checkbox" checked={!!b2} onChange={() => setCheck(0)} />
                </div>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <input type="checkbox" checked={!!c1} onChange={() => setCheck(1)} />
                </div>

                <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                  <input type="checkbox" checked={!!d1} onChange={() => setCheck(2)} />
                  <input type="checkbox" checked={!!d2} onChange={() => setCheck(3)} />
                </div>

                <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                  <input type="checkbox" checked={!!e1} onChange={() => setCheck(4)} />
                  <input type="checkbox" checked={!!e2} onChange={() => setCheck(5)} />
                  <input type="checkbox" checked={!!e3} onChange={() => setCheck(6)} />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Nemesis table */}
      <div style={{ marginTop: 14 }}>
        <b>Nemesis</b>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "240px 140px 140px 140px",
            gap: 8,
            marginTop: 8,
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: 700 }}>Monster</div>
          <div style={{ fontWeight: 700 }}>
            Lvl 1
            <br />
            n3 CC
          </div>
          <div style={{ fontWeight: 700 }}>
            Lvl 2
            <br />
            n3 CC
          </div>
          <div style={{ fontWeight: 700 }}>
            Lvl 3
            <br />
            n3 CC
          </div>

          {ccNemesisSlots.map((slot, rowIdx) => {
            const base = rowIdx * 3;
            const b = ccNemesisChecks[base + 0];
            const c = ccNemesisChecks[base + 1];
            const d = ccNemesisChecks[base + 2];

            const setCheck = (offset) =>
              updateSettlement({ ccNemesisChecks: toggleBoolAt(ccNemesisChecks, base + offset) });

            const setSlotName = (name) => {
              const next = ccNemesisSlots.map((s) => (s.id === slot.id ? { ...s, name } : s));
              updateSettlement({ ccNemesisSlots: next });
            };

            return (
              <React.Fragment key={slot.id}>
                <select value={slot.name || ""} onChange={(e) => setSlotName(e.target.value)} style={{ padding: 8 }}>
                  <option value="">Select Nemesis</option>
                  {ccNemesisOptions.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <input type="checkbox" checked={!!b} onChange={() => setCheck(0)} />
                </div>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <input type="checkbox" checked={!!c} onChange={() => setCheck(1)} />
                </div>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <input type="checkbox" checked={!!d} onChange={() => setCheck(2)} />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* CC Rewards */}
      <div style={{ marginTop: 14 }}>
        <b>Collective Cognition Rewards</b>
        <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
          {CC_REWARDS.map((r) => {
            const checked = !!ccRewardsChecked[r.at];
            return (
              <label key={r.at} style={{ display: "inline-flex", alignItems: "center", gap: 8, width: "fit-content" }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    updateSettlement({
                      ccRewardsChecked: { ...ccRewardsChecked, [r.at]: !checked },
                    })
                  }
                />
                <span>
                  {r.at} - {r.name}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Philosophy */}
      <SectionBox title="Philosophy">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={() => {
              ensureAtLeastOnePhilosophyRow();
              addPhilosophyRow();
            }}
            style={{ marginLeft: "auto" }}
          >
            + Add Philosophy
          </button>
        </div>

        {philosophyRows.length === 0 ? (
          <div style={{ marginTop: 6 }}>
            <button type="button" onClick={ensureAtLeastOnePhilosophyRow} style={{ padding: "6px 10px" }}>
              Add your first Philosophy
            </button>
          </div>
        ) : (
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
            {philosophyRows.map((row) => {
              const entry = row?.name ? getPhilosophyEntry(row.name) : null;

              return (
                <div key={row.id} style={{ border: "1px solid #ddd", padding: 10 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select
                      value={row.name || ""}
                      onChange={(e) => updatePhilosophyRowName(row.id, e.target.value)}
                      style={{ flex: 1, padding: 8 }}
                    >
                      <option value="">Select Philosophy</option>
                      {philosophyNames.map((nm) => (
                        <option key={nm} value={nm}>
                          {nm}
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={() => removePhilosophyRow(row.id)}>
                      Remove
                    </button>
                  </div>
                  {entry ? <CardText entry={entry} /> : null}
                </div>
              );
            })}
          </div>
        )}
      </SectionBox>

      {/* Knowledge */}
      <SectionBox title="Knowledge">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={() => {
              ensureAtLeastOneKnowledgeRow();
              addKnowledgeRow();
            }}
            style={{ marginLeft: "auto" }}
          >
            + Add Knowledge
          </button>
        </div>

        {settlementKnowledges.length === 0 ? (
          <div style={{ marginTop: 6 }}>
            <button type="button" onClick={ensureAtLeastOneKnowledgeRow} style={{ padding: "6px 10px" }}>
              Add your first Knowledge
            </button>
          </div>
        ) : (
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
            {settlementKnowledges.map((row) => {
              const levelsForName = row?.name ? knowledgeByName[row.name] || [] : [];
              const distinctLevels = Array.from(
                new Set(levelsForName.map((k) => String(k.level || "").trim()).filter(Boolean))
              );
              const hasLevelChoice = distinctLevels.length > 1;

              const entry = getKnowledgeEntry(row?.name, row?.level);

              const effectiveLevel = hasLevelChoice
                ? String(row.level || "")
                : entry?.level
                ? String(entry.level).trim()
                : "";

              return (
                <div key={row.id} style={{ border: "1px solid #ddd", padding: 10 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select
                      value={row.name || ""}
                      onChange={(e) => {
                        const newName = e.target.value;
                        const list = knowledgeByName[newName] || [];
                        const first = list[0] || null;

                        const newDistinct = Array.from(
                          new Set(list.map((k) => String(k.level || "").trim()).filter(Boolean))
                        );

                        const defaultLevel =
                          newDistinct.length > 0
                            ? newDistinct[0]
                            : first?.level
                            ? String(first.level).trim()
                            : "";

                        updateKnowledgeRow(row.id, { name: newName, level: defaultLevel });
                      }}
                      style={{ flex: 1, padding: 8 }}
                    >
                      <option value="">Select Knowledge</option>
                      {knowledgeNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>

                    <select
                      value={effectiveLevel}
                      disabled={!row.name || !hasLevelChoice}
                      onChange={(e) => updateKnowledgeRow(row.id, { level: e.target.value })}
                      style={{
                        width: 120,
                        padding: 8,
                        opacity: !row.name || !hasLevelChoice ? 0.45 : 1,
                        pointerEvents: !row.name || !hasLevelChoice ? "none" : "auto",
                        background: !row.name || !hasLevelChoice ? "#f2f2f2" : undefined,
                      }}
                    >
                      {!row.name ? (
                        <option value="">Level</option>
                      ) : hasLevelChoice ? (
                        distinctLevels.map((lvl) => (
                          <option key={`${row.name}-${lvl}`} value={lvl}>
                            {lvl}
                          </option>
                        ))
                      ) : (
                        <option value={effectiveLevel}>{effectiveLevel || "-"}</option>
                      )}
                    </select>

                    <button type="button" onClick={() => removeKnowledgeRow(row.id)}>
                      Remove
                    </button>
                  </div>

                  {entry ? <CardText entry={entry} /> : null}
                </div>
              );
            })}
          </div>
        )}
      </SectionBox>
    </SectionBox>
  ) : null;

  return (
    <div style={{ padding: 16 }}>
      <InlineStyleGuard />

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Settlement Sheet</h2>

        <button style={{ marginLeft: "auto" }} onClick={() => nav("/departing")}>
          Go to Departing Survivors
        </button>
        <button onClick={() => nav("/timeline")}>Open Timeline</button>
        <button onClick={() => nav("/compendium")}>Open Compendium</button>
      </div>

      {error ? <div style={{ color: "crimson", marginTop: 8 }}>{error}</div> : null}

      <div style={{ marginTop: 10 }}>
        <div>
          <b>Settlement:</b> {current.settlementName}
        </div>
        <div>
          <b>Campaign:</b> {current.campaignType || "-"}
        </div>
        <div>
          <b>Pillars:</b> {pillars.length ? pillars.join(", ") : "None"}
        </div>
      </div>

      <hr style={{ margin: "14px 0" }} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(260px, 1fr))", gap: 12 }}>
        {/* LEFT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SectionBox title="Core">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
              <b>Survival Limit</b>
              <TwoCharNumber
                value={settlement.survivalLimit}
                onChange={(v) => updateSettlement({ survivalLimit: v })}
                title="Survival limit (0-99)"
              />
            </div>

            <div style={{ marginTop: 12 }}>
              <b>Death Count</b>
              <CheckboxGrid
                values={deathBoxes.slice(0, 28)}
                columns={28}
                boldIndexes={[0]}
                onToggle={(i) => updateSettlement({ deathBoxes: toggleBoolAt(deathBoxes, i), deathCount: undefined })}
              />
              <CheckboxGrid
                values={deathBoxes.slice(28, 56)}
                columns={28}
                onToggle={(i) =>
                  updateSettlement({ deathBoxes: toggleBoolAt(deathBoxes, 28 + i), deathCount: undefined })
                }
              />
            </div>

            {/* Quarries: ONLY checkbox + text clickable */}
            <div style={{ marginTop: 14 }}>
              <b>Quarries</b>
              <div style={{ fontSize: 13, opacity: 0.85, marginTop: 3 }}>
                The monster your settlement can select to hunt
              </div>

              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                {allQuarryNamesFiltered.map(({ name, node }) => {
                  const checked = !!quarriesChecked[name];
                  return (
                    <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, width: "fit-content" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          updateSettlement({ quarriesChecked: { ...quarriesChecked, [name]: !checked } })
                        }
                        style={{ cursor: "pointer" }}
                      />
                      <span
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() =>
                          updateSettlement({ quarriesChecked: { ...quarriesChecked, [name]: !checked } })
                        }
                      >
                        <b>{name}</b> <span style={{ opacity: 0.75 }}>(Node {node})</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Nemesis */}
            <div style={{ marginTop: 14 }}>
              <b>Nemesis</b>
              <div style={{ fontSize: 13, opacity: 0.85, marginTop: 3 }}>
                The nemesis monster your settlement can encounter.
              </div>

              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                {allNemesisNamesFiltered.map(({ name, node }) => {
                  const row = nemesisProgress[name] || { checked: false, lvl1: false, lvl2: false, lvl3: false };
                  const setRow = (patch) =>
                    updateSettlement({
                      nemesisProgress: {
                        ...nemesisProgress,
                        [name]: { ...row, ...patch },
                      },
                    });

                  return (
                    <div key={name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 240, width: "fit-content" }}>
                        <input
                          type="checkbox"
                          checked={!!row.checked}
                          onChange={() => setRow({ checked: !row.checked })}
                          style={{ cursor: "pointer" }}
                        />
                        <span
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => setRow({ checked: !row.checked })}
                        >
                          <b>{name}</b> <span style={{ opacity: 0.75 }}>(Node {node})</span>
                        </span>
                      </div>

                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content" }}>
                        <input type="checkbox" checked={!!row.lvl1} onChange={() => setRow({ lvl1: !row.lvl1 })} />
                        <span>Lvl 1</span>
                      </label>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content" }}>
                        <input type="checkbox" checked={!!row.lvl2} onChange={() => setRow({ lvl2: !row.lvl2 })} />
                        <span>Lvl 2</span>
                      </label>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content" }}>
                        <input type="checkbox" checked={!!row.lvl3} onChange={() => setRow({ lvl3: !row.lvl3 })} />
                        <span>Lvl 3</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Campaign Milestones */}
            <div style={{ marginTop: 14 }}>
              <b>Campaign Milestones</b>
              <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                {(isDreamKeeper
                  ? [
                      { key: "pop0", text: "Population Reaches 0 📖 Game Over" },
                      { key: "pop15", text: "Population Reaches 15 📖 Principle: Society" },
                      { key: "firstChild", text: "First child is born 📖 Principle: New Life" },
                      { key: "firstDeathUpdate", text: "First time death count is updated 📖 Principle Death" },
                      {
                        key: "first3Understanding",
                        text: "First survivor to reach 3 understanding 📖 Designs & Dandelions",
                      },
                    ]
                  : [
                      { key: "pop0", text: "Population Reaches 0 📖 Game Over" },
                      { key: "pop15", text: "Population Reaches 15 📖 Principle: Society" },
                      { key: "firstChild", text: "First child is born 📖 Principle: New Life" },
                      { key: "firstDeathUpdate", text: "First time death count is updated 📖 Principle Death" },
                      { key: "fiveInnovations", text: "Settlement has 5 Innovations 📖 Hooded Knight" },
                    ]
                ).map((m) => {
                  const checked = !!milestoneChecks[m.key];
                  return (
                    <label key={m.key} style={{ display: "inline-flex", alignItems: "center", gap: 8, width: "fit-content" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          updateSettlement({ campaignMilestones: { ...milestoneChecks, [m.key]: !checked } })
                        }
                      />
                      <span>{m.text}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Principles (with Innovation.js card text) */}
            <div style={{ marginTop: 14 }}>
              <b>Principles</b>
              <div style={{ fontSize: 13, opacity: 0.85, marginTop: 3 }}>The settlement&apos;s established principles.</div>

              <div style={{ marginTop: 10, display: "grid", gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 800 }}>New Life</div>
                  <select
                    value={principles.newLife || ""}
                    onChange={(e) => updateSettlement({ principles: { ...principles, newLife: e.target.value } })}
                    style={{ width: "100%", padding: 8, marginTop: 4 }}
                  >
                    <option value="">Select...</option>
                    <option value="Protect the Young">Protect the Young</option>
                    <option value="Survival of the Fittest">Survival of the Fittest</option>
                  </select>
                  {principles.newLife ? <InnovationCardText entry={getInnovationEntry(principles.newLife)} /> : null}
                </div>

                <div>
                  <div style={{ fontWeight: 800 }}>Death</div>
                  <select
                    value={principles.death || ""}
                    onChange={(e) => updateSettlement({ principles: { ...principles, death: e.target.value } })}
                    style={{ width: "100%", padding: 8, marginTop: 4 }}
                  >
                    <option value="">Select...</option>
                    <option value="Graves">Graves</option>
                    <option value="Cannibalize">Cannibalize</option>
                  </select>
                  {principles.death ? <InnovationCardText entry={getInnovationEntry(principles.death)} /> : null}
                </div>

                <div>
                  <div style={{ fontWeight: 800 }}>Society</div>
                  <select
                    value={principles.society || ""}
                    onChange={(e) => updateSettlement({ principles: { ...principles, society: e.target.value } })}
                    style={{ width: "100%", padding: 8, marginTop: 4 }}
                  >
                    <option value="">Select...</option>
                    <option value="Collective Toil">Collective Toil</option>
                    <option value="Accept Darkness">Accept Darkness</option>
                  </select>
                  {principles.society ? <InnovationCardText entry={getInnovationEntry(principles.society)} /> : null}
                </div>

                <div>
                  <div style={{ fontWeight: 800 }}>Conviction</div>
                  <select
                    value={principles.conviction || ""}
                    onChange={(e) => updateSettlement({ principles: { ...principles, conviction: e.target.value } })}
                    style={{ width: "100%", padding: 8, marginTop: 4 }}
                  >
                    <option value="">Select...</option>
                    <option value="Romantic">Romantic</option>
                    <option value="Barbaric">Barbaric</option>
                  </select>
                  {principles.conviction ? <InnovationCardText entry={getInnovationEntry(principles.conviction)} /> : null}
                </div>
              </div>
            </div>

            {showLanternResearch ? (
              <div style={{ marginTop: 14 }}>
                <b>Lantern Research Level</b>
                <div style={{ marginTop: 6 }}>
                  <TwoCharNumber
                    value={settlement.lanternResearchLevel}
                    onChange={(v) => updateSettlement({ lanternResearchLevel: v })}
                    title="Lantern Research Level (0-99)"
                  />
                </div>
              </div>
            ) : null}

            <div style={{ marginTop: 14 }}>
              <b>Notes</b>
              <textarea
                value={settlement.notes || ""}
                onChange={(e) => updateSettlement({ notes: e.target.value })}
                style={{ width: "100%", padding: 8, marginTop: 6, minHeight: 90 }}
              />
            </div>
          </SectionBox>

          {/* Innovations */}
          <SectionBox title="Innovations" subtitle="Select innovations. The Innovation card text is shown below each selection.">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <b>Innovation</b>
              <button
                type="button"
                onClick={() => {
                  ensureAtLeastOneInnovationRow();
                  addInnovationRow();
                }}
                style={{ marginLeft: "auto" }}
              >
                + Add Innovation
              </button>
            </div>

            {innovationRows.length === 0 ? (
              <div style={{ marginTop: 6 }}>
                <button type="button" onClick={ensureAtLeastOneInnovationRow} style={{ padding: "6px 10px" }}>
                  Add your first Innovation
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
                {innovationRows.map((row) => {
                  const entry = row?.name ? innovationsByName[row.name] || null : null;

                  return (
                    <div key={row.id} style={{ border: "1px solid #ddd", padding: 10 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <select
                          value={row.name || ""}
                          onChange={(e) => updateInnovationRowName(row.id, e.target.value)}
                          style={{ flex: 1, padding: 8 }}
                        >
                          <option value="">Select Innovation</option>
                          {innovationNames.map((nm) => (
                            <option key={nm} value={nm}>
                              {nm}
                            </option>
                          ))}
                        </select>

                        <button type="button" onClick={() => removeInnovationRow(row.id)}>
                          Remove
                        </button>
                      </div>

                      {entry ? <InnovationCardText entry={entry} /> : null}
                    </div>
                  );
                })}
              </div>
            )}
          </SectionBox>

          {/* Settlement Locations */}
          <SectionBox title="Settlement Locations" subtitle="Selected one at a time (dropdown).">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <b>Settlement Location</b>
              <button
                type="button"
                onClick={() => {
                  ensureAtLeastOneLocationRow();
                  addLocationRow();
                }}
                style={{ marginLeft: "auto" }}
              >
                + Add Location
              </button>
            </div>

            {locationRows.length === 0 ? (
              <div style={{ marginTop: 6 }}>
                <button type="button" onClick={ensureAtLeastOneLocationRow} style={{ padding: "6px 10px" }}>
                  Add your first Location
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
                {locationRows.map((row) => (
                  <div key={row.id} style={{ border: "1px solid #ddd", padding: 10 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select
                        value={row.name || ""}
                        onChange={(e) => updateLocationRowName(row.id, e.target.value)}
                        style={{ flex: 1, padding: 8 }}
                      >
                        <option value="">Select Settlement Location</option>
                        {settlementLocationOptions.map((key) => (
                          <option key={key} value={key}>
                            {prettyKeyToTitle(key)}
                          </option>
                        ))}
                      </select>

                      <button type="button" onClick={() => removeLocationRow(row.id)}>
                        Remove
                      </button>
                    </div>

                    {row?.name && locationByKey[row.name] ? <CardText entry={locationByKey[row.name]} /> : null}
                  </div>
                ))}
              </div>
            )}
          </SectionBox>

          {/* Patterns */}
          <SectionBox title="Patterns" subtitle="Blank for now.">
            <textarea
              value={settlement.patterns || ""}
              onChange={(e) => updateSettlement({ patterns: e.target.value })}
              style={{ width: "100%", padding: 8, marginTop: 8, minHeight: 90 }}
            />
          </SectionBox>

          {/* Seed Patterns */}
          {hasSeedPatterns ? (
            <SectionBox title="Seed Patterns" subtitle="Blank for now.">
              <textarea
                value={settlement.seedPatterns || ""}
                onChange={(e) => updateSettlement({ seedPatterns: e.target.value })}
                style={{ width: "100%", padding: 8, marginTop: 8, minHeight: 90 }}
              />
            </SectionBox>
          ) : null}
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SectionBox title="Resource Storage" subtitle="Blank for now.">
            <textarea
              value={settlement.resourceStorage || ""}
              onChange={(e) => updateSettlement({ resourceStorage: e.target.value })}
              style={{ width: "100%", padding: 8, marginTop: 8, minHeight: 90 }}
            />
          </SectionBox>

          {/* Gear Storage */}
          <SectionBox
            title="Gear Storage"
            subtitle="ONE dropdown to add gear. Display grouped/sorted by craft location; quantity box (1-9) per item."
          >
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6 }}>
              <select
                value={gearToAdd}
                onChange={(e) => setGearToAdd(e.target.value)}
                style={{ flex: 1, padding: 8 }}
              >
                <option value="">Select Gear</option>
                {gearNames.map((nm) => (
                  <option key={nm} value={nm}>
                    {gearOptionLabel(nm)}
                  </option>
                ))}
              </select>

              <button type="button" onClick={addSelectedGear} disabled={!gearToAdd}>
                Add
              </button>
            </div>

            {gearStorageItems.length === 0 ? (
              <div style={{ marginTop: 10, opacity: 0.75, fontSize: 13 }}>No gear added yet.</div>
            ) : (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
                {gearGroups.map((grp) => (
                  <div key={grp.loc} style={{ border: "1px solid #ddd", padding: 10 }}>
                    <div style={{ fontWeight: 800, marginBottom: 8 }}>{grp.loc}</div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {grp.items.map((item) => (
                        <div
                          key={item.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            border: "1px solid #eee",
                            padding: "6px 8px",
                          }}
                        >
                          <input
                            className="qtyBox"
                            type="number"
                            min={1}
                            max={9}
                            value={clampInt(item.qty ?? 1, 1, 9)}
                            onChange={(e) => updateGearQty(item.id, e.target.value)}
                            title="Quantity (1-9)"
                          />

                          <div style={{ flex: 1 }}>
                            <b>{item.name}</b>
                          </div>

                          <button type="button" onClick={() => removeGearItem(item.id)}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionBox>

          {/* ✅ Arc Survivors moved to bottom of right column */}
          {ArcSurvivorsBlock}
        </div>
      </div>

      {/* Population ABOVE survivors */}
      <hr style={{ margin: "14px 0" }} />
      <div style={{ border: "1px solid #ddd", padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <b>{useArc ? "Population (Arc Survivors)" : "Population"}</b>
          <TwoCharNumber
            value={settlement.population}
            onChange={(v) => updateSettlement({ population: v })}
            title="Population (0-99)"
          />
        </div>
      </div>

      <SurvivorsPanel newName={newName} setNewName={setNewName} handleAdd={handleAdd} openAllSheets={openAllSheets} closeAllSheets={closeAllSheets} limit={limit} departingIds={departingIds} aliveSurvivors={aliveSurvivors} deceasedSurvivors={deceasedSurvivors} openSheetIds={openSheetIds} toggleDepartingWithGuard={toggleDepartingWithGuard} updateSurvivor={updateSurvivor} campaignKeyBase={campaignKeyBase} useArc={useArc} toggleInlineSheet={toggleInlineSheet} removeSurvivor={removeSurvivor} />

      {/* Lost Settlements LAST */}
      <hr style={{ margin: "14px 0" }} />
      <div style={{ border: "1px solid #ddd", padding: 12 }}>
        <b>Lost Settlements</b>
        <div style={{ marginTop: 8 }}>
          <CheckboxGrid
            values={lostSettlementBoxes}
            columns={20}
            boldIndexes={[4, 9, 14, 19]}
            onToggle={(i) => updateSettlement({ lostSettlementBoxes: toggleBoolAt(lostSettlementBoxes, i) })}
            compact={false}
          />
        </div>
      </div>
    </div>
  );
}
